import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  inject,
  model,
  OnInit, signal,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  ButtonComponent,
  ButtonOptions,
  DateComponent, DateOptions,
  FormComponent, InputComponent, InputOptions,
  ModalComponent,
  ModalOptions
} from "@shared/components";
import {DropdownComponent, DropdownOptions} from "@shared/components/dropdown/dropdown.component";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  NgForm,
  ReactiveFormsModule, ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {Coding} from '@core/classes/other/coding';
import {APIService, DataService, ToastService} from '@core/services';
import {PatientService} from '@core/services/patient.service';
import {HTTPMethods, InputTypes, ModalTypes} from '@core/enums';
import {HttpHeaders} from '@angular/common/http';
import {DefaultSearchComponent} from '@shared/components/dropdown/search/default-search/default-search.component';
import {ATC} from '@core/classes/other/atc';
import {lastValueFrom} from 'rxjs';
import {Response, ResponseSingle} from '@core/classes/other/response';
import {assets} from '@core/constants';
import {Contact} from '@core/classes/tables/contact';

@Component({
  selector: 'ncp-add-contact',
  imports: [
    ButtonComponent,
    DropdownComponent,
    FormsModule,
    ModalComponent,
    ReactiveFormsModule,
    InputComponent
  ],
  templateUrl: './add-contact.component.html',
  styleUrl: './add-contact.component.scss',
})
export class AddContactComponent implements OnInit, AfterViewInit {
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('cFrm') protected contactsFormEl?: NgForm;
  @ViewChild('addContactButton') public addContactButton!: ButtonComponent;

  @ViewChild('name') public nameIn!: InputComponent;
  @ViewChild('surname') public surnameIn!: InputComponent;
  @ViewChild('address') public addressIn!: InputComponent;
  @ViewChild( 'countryDesc') public countryDesc!: DropdownComponent<Coding>;
  @ViewChild('phone') public phoneIn!: InputComponent;
  @ViewChild('email') public emailIn!: InputComponent;

  protected readonly toastService = inject(ToastService);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private apiService = inject(APIService);
  private patientService = inject(PatientService);

  protected data = model<any>();

  protected form: FormGroup = new FormGroup({
    given_name: new FormControl<string>(''  ,[Validators.required]),
    family_name_surname: new FormControl<string>('' ,[Validators.required]),
    address: new FormControl<string>('',[Validators.required]),
    country_id: new FormControl<number | string | undefined>(undefined ,[Validators.required]),
    telephone_no: new FormControl<string>('' ,[Validators.required]),
    email: new FormControl<string>('',[Validators.required]),
  }, {
    validators: [this.formValidator()]
  });

  protected cLoading: boolean = false;

  getFormControl = (name: string) => {
    return this.form.get(name) as FormControl;
  }


  public btnCOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'cBtn',
      tooltip: 'add_contact',
    })
  );

  protected nameInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'name',
      label: {
        text: 'given_name',
      },
      type: InputTypes.text,
      name: 'given_name',
      formControl: this.getFormControl('given_name'),
      initialValue: '',
      required: true,
    })
  );

  protected surnameInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'surname',
      label: {
        text: 'family_name',
      },
      type: InputTypes.text,
      name: 'family_name',
      formControl: this.getFormControl('family_name_surname'),
      initialValue: '',
      required: true,
    })
  );

  protected addressInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'address',
      label: {
        text: 'address',
      },
      type: InputTypes.text,
      name: 'address',
      formControl: this.getFormControl('address'),
      initialValue: '',
      required: true,
    })
  );

  protected phoneInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'telephone_number',
      label: {
        text: 'telephone_number',
      },
      type: InputTypes.tel,
      name: 'telephone_number',
      formControl: this.getFormControl('telephone_no'),
      initialValue: '',
      required: true,
    })
  );

  protected emailInputOptions = signal<InputOptions>(
    new InputOptions({
      id: 'email',
      label: {
        text: 'email',
      },
      type: InputTypes.text,
      name: 'email',
      formControl: this.getFormControl('email'),
      initialValue: '',
      required: true,
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'modal',
      type: ModalTypes.form,
      form: FormComponent,
      title: "add_contact",
      options: {
        animation: true,
        size: 'xl',
        fullscreen: "lg",
        backdrop: 'static',
        keyboard: false,
      },
      onClose: () => {this.onFormReset()}
    })
  );

  protected countryDescDDOptions = signal<DropdownOptions<Coding>>(
    new DropdownOptions<Coding>({
      id: 'countryDescription',
      name: 'countryDescription',
      placeholder: 'country_description',
      label: {
        id: "country_desc_label",
        text: 'country_description',
      },
      api: {
        path: '/get-all_codings_no_limit',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.4',
          description: ''
        },
      },
      required: true,
      searchComponent: DefaultSearchComponent,
      formControl: this.form.get('country_id') as FormControl
    })
  );


  protected onSetDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'onSetDate',
      label: {
        text: 'on_set',
      },
      name: 'onSetDate',
      initialValue: ''
    })
  );

  ngOnInit() {
    this.btnCOptions().onClick = (event) => {
      this.mod.options().title = 'add_contact';
      this.mod.options().onReset = this.onFormReset;
      this.addContactButton.blur();
      this.cLoading = true;
      this.downloadDropDownData().then((value) => {
        this.cLoading = false;
        this.changeDetectorRef.detectChanges();
        this.mod.open();
      });
    };

    this.modOptions().onSave = this.onSave;
    this.modOptions().onShow = this.downloadDropDownData;
  }

  formValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};

      let givenName = (control as FormGroup).controls['given_name'];
      let familyNameSurname = (control as FormGroup).controls['family_name_surname'];
      let telephoneNo = (control as FormGroup).controls['telephone_no'];
      let countryId = (control as FormGroup).controls['country_id'];
      let address = (control as FormGroup).controls['address'];
      let email = (control as FormGroup).controls['email'];

      if(givenName.value === undefined || givenName.value === null || givenName.value === '') {
        givenName.setErrors({cp_givenNameInvalid: true});
        errors['cp_givenNameInvalid'] = true;
      }

      if(familyNameSurname.value === undefined || familyNameSurname.value === null || familyNameSurname.value === '') {
        familyNameSurname.setErrors({cp_familyNameSurnameInvalid: true});
        errors['cp_familyNameSurnameInvalid'] = true;
      }

      if(address.value === undefined || address.value === null || address.value === '') {
        address.setErrors({cp_addressInvalid: true});
        errors['cp_addressInvalid'] = true;
      }

      if(countryId.value === undefined || countryId.value === null) {
        countryId.setErrors({cp_countryIdInvalid: true});
        errors['cp_countryIdInvalid'] = true;
      }

      if(telephoneNo.value === undefined || telephoneNo.value === null || telephoneNo.value === '') {
        telephoneNo.setErrors({cp_telephoneNoInvalid: true});
        errors['cp_telephoneNoInvalid'] = true;
      }

      if(email.value === undefined || email.value === null || email.value === '') {
        email.setErrors({cp_emailInvalid: true});
        errors['cp_emailInvalid'] = true;
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }
  }

  onFormReset = () => {
    console.log('on reset form');
    this.form.reset();
    this.nameIn.reset();
    this.surnameIn.reset();
    this.addressIn.reset();
    this.countryDesc.clearSelection();
    this.phoneIn.reset();
    this.emailIn.reset();

    this.modOptions().errors = [];
  }

  ngAfterViewInit(): void {

  }

  protected downloadDropDownData = async () => {
    let ddData = JSON.parse(localStorage.getItem('dropDownData')!) as Object || {};

    if(ddData.hasOwnProperty('countryDescription')) {
      return;
    }

    const data = await lastValueFrom(this.apiService.call({
      path: '/get-all_codings_no_limit',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      body: {
        oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.4',
        description: ''
      }
    })!);

    if (data && data.status === 1 && data.data.length > 0) {
      Object.defineProperty(ddData, 'countryDescription', {
        value: (data as Response<Coding>).data,
        enumerable: true,
        writable: false
      });
      this.countryDesc.data = (data as Response<Coding>).data || [];

      localStorage.setItem('dropDownData', JSON.stringify(ddData));
    }
  }

  onSave = () => {
    this.contactsFormEl?.ngSubmit.emit();
  }

  validate = (): boolean => {
    Object.keys(this.form.controls).forEach((control) => {
      this.form.controls[control].markAsTouched();
    })

    return true;
  }

  onFormSubmit = () => {
    if(this.validate() && this.form.invalid) {
      console.log('this.form.is.invalid');
      this.modOptions().errors = this.form.errors!;
      this.mod.detectChanges();
      return;
    }

    this.modOptions().disableOnSave = true;
    var body: {[key: string]: any} = {
      given_name: this.form.get('given_name')?.value,
      family_name_surname: this.form.get('family_name_surname')?.value,
      telephone_no: this.form.get('telephone_no')?.value,
      address: this.form.get('address')?.value,
      country_id: this.countryDescDDOptions().value!.coding_id,
      country: this.countryDescDDOptions().value!.description,
      email: this.form.get('email')?.value,
      status: 0, // send a 0 status as we are not checking for max 3 here
      version_id: this.patientService.version!.new,
    }

    this.apiService.call({
      path: '/cp',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      type: HTTPMethods.POST,
      body: body
    })?.subscribe((res: ResponseSingle<Contact>) => {
      if(res && res.status?.toString() === '1') {
        let data = this.data()?.tblOptions().data
        data.unshift(res.data)
        if (this.data && this.data() && this.data().tbl && this.data().tbl.tableData) {
          this.data()!.tbl!.tableData! = data;
        }
        this.mod.close();
      }
    });
  }

  protected readonly assets = assets;
}
